import { useEffect, useState } from "react";
import { IStandardsSetListViewModel } from "@/search/NewSearchPageMVP/types/IStandardsSetListViewModel";
import {
  IStandardsSetMode,
  STANDARDS_SET_MODE,
} from "@/search/NewSearchPageMVP/types/IStandardsSetMode";
import { IStandardsModalConfirmData } from "@/search/NewSearchPageMVP/types/IStandardsModalConfirmData";

import { convertStandardsSetsFetchToViewModel } from "@/search/NewSearchPageMVP/services/StandardsSets/utils";
import { useStandardsSetAPI } from "./useStandardsSetAPI";

export const useStandardsSet = () => {
  const [newStandardsSet, setNewStandardsSet] =
    useState<IStandardsSetListViewModel>({
      id: "",
      title: "",
      children: [],
      viewmodel_type: "list_type",
    });
  const [createdStandardsSets, setCreatedStandardsSets] = useState<
    IStandardsSetListViewModel[]
  >([]);
  const [standardsSetTitleError, setStandardsSetTitleError] = useState<
    string | null
  >(null);
  const [isEditingSet, setIsEditingSet] = useState(false);
  const [standardsSetMode, setStandardsSetMode] = useState<IStandardsSetMode>(
    STANDARDS_SET_MODE.VIEW,
  );
  const [confirmModalData, setConfirmModalData] =
    useState<IStandardsModalConfirmData>({});
  const [isConfirmActionModalOpen, setIsConfirmActionModalOpen] =
    useState(false);
  const {
    draftStandardsSetId,
    handleAddStandardsRelationsToSet,
    handleCreateDraftStandardsSet,
    handleDeleteStandardsRelationsFromSet,
    handlePublishStandardsSet,
    handleUnpublishStandardsSet,
    handleUpdateStandardsSetTitle,
    setDraftStandardsSetId,
    standardsSets,
    operationErrorMessage,
    resetOperationErrorMessage,
  } = useStandardsSetAPI(confirmModalData.editData);

  const [isPublishingStandardsSet, setIsPublishingStandardsSet] =
    useState(false);
  const [isUpdatingStandardsSet, setIsUpdatingStandardsSet] = useState(false);
  const [isDeletingStandardsSet, setIsDeletingStandardsSet] = useState(false);

  const getCreatedStandardsSets = () => {
    if (operationErrorMessage.current !== "") return;

    if (standardsSets) {
      const convertedSetsToViewmodel =
        convertStandardsSetsFetchToViewModel(standardsSets);
      setCreatedStandardsSets(convertedSetsToViewmodel);
    } else {
      setCreatedStandardsSets([]);
    }
  };

  const addNewStandardsSet = async (newStdSet: IStandardsSetListViewModel) => {
    if (draftStandardsSetId) {
      setIsPublishingStandardsSet(true);
      const standardsIds = newStdSet.children.map(
        (standard) => standard.content_id,
      );
      await handleAddStandardsRelationsToSet(
        newStandardsSet.id,
        standardsIds,
        0,
      );
      if (operationErrorMessage.current !== "") return;

      await handleUpdateStandardsSetTitle(newStandardsSet.id, newStdSet.title);
      if (operationErrorMessage.current !== "") return;

      await handlePublishStandardsSet(draftStandardsSetId);
      if (operationErrorMessage.current !== "") return;

      setCreatedStandardsSets([...createdStandardsSets, newStdSet]);
      setIsPublishingStandardsSet(false);
    } else {
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const editStandardsSet = async (
    editedStandardsSetData: IStandardsSetListViewModel,
  ) => {
    setIsUpdatingStandardsSet(true);
    const currentStandardsSet = createdStandardsSets.find(
      (std) => std.id === editedStandardsSetData.id,
    );
    if (!currentStandardsSet) return;

    const currentStandardsInSet = currentStandardsSet.children.map(
      (std) => std.content_id,
    );
    const newStandardsInSet = editedStandardsSetData.children.map(
      (std) => std.content_id,
    );

    const removedIds = currentStandardsInSet.filter(
      (id) => !newStandardsInSet.includes(id),
    );
    const newIds = newStandardsInSet.filter(
      (id) => !currentStandardsInSet.includes(id),
    );

    const standardsSetId = confirmModalData.editData?.id;
    if (!standardsSetId) return;

    let setUpdated = false;
    // If there is any standard removed from the set, delete content relation
    if (removedIds.length) {
      await handleDeleteStandardsRelationsFromSet(standardsSetId, removedIds);
      setUpdated = true;
    }
    if (operationErrorMessage.current !== "") return;

    // If there is any standard added to the set, create content relation
    if (newIds.length) {
      await handleAddStandardsRelationsToSet(
        standardsSetId,
        newIds,
        newStandardsInSet.length,
      );
      setUpdated = true;
    }
    if (operationErrorMessage.current !== "") return;

    // If title was changed, update the content
    if (currentStandardsSet.title !== editedStandardsSetData.title) {
      await handleUpdateStandardsSetTitle(
        standardsSetId,
        confirmModalData?.editData?.title || "",
      );
      setUpdated = true;
    }

    if (operationErrorMessage.current !== "") return;

    // If any of the possible updates happened, republish the set
    if (setUpdated) {
      await handlePublishStandardsSet(standardsSetId);
    }

    if (operationErrorMessage.current !== "") return;

    // If API operations were successful, update local state
    const newCreatedStandardsSets = createdStandardsSets.map((std) =>
      std.id === editedStandardsSetData.id
        ? {
            id: editedStandardsSetData.id,
            title: editedStandardsSetData.title,
            children: editedStandardsSetData.children,
            viewmodel_type: editedStandardsSetData.viewmodel_type,
          }
        : std,
    );
    setCreatedStandardsSets(newCreatedStandardsSets);
    setIsEditingSet(false);
    setIsUpdatingStandardsSet(false);
  };

  const moveToAllTabBeforeDelete = (
    callback: (standardsSetId: string) => void,
    standardsSetId: string,
  ) => {
    // If we delete the current standards set while in the current tab, Angelou will throw
    // 'invalid panelId set for selectedPanelId' error. So to do it without errors,
    // We first "go" to the All tab, but hiding it's content and not showing to the user,
    // and then run the callback method, which is the actual logic to delete the set.
    // When the set is deleted, it'll trigger useStandardsModalTabsNavigation hook to
    // go to the correct tab.
    // Once that is done, we remove the display none from the all tab content
    const allButton = document.getElementById("all_tab");
    const allContent = document.getElementById("all");
    const parentElement = allButton?.parentElement;
    if (allButton && allContent && parentElement) {
      const prevDisplayAttr = allContent.style.display;
      allContent.style.display = "none";
      allButton.click();
      parentElement.dataset["selected"] = "false";
      callback(standardsSetId);
      allContent.style.display = prevDisplayAttr;
    }
  };

  const deleteStandardsSet = async (standardsSetId: string) => {
    moveToAllTabBeforeDelete(async (standardsSetId) => {
      setIsDeletingStandardsSet(true);
      await handleUnpublishStandardsSet(standardsSetId);

      if (operationErrorMessage.current !== "") return;

      const newCreatedStandardsSets = createdStandardsSets.filter(
        (std) => std.id !== standardsSetId,
      );
      setIsEditingSet(false);
      setCreatedStandardsSets([...newCreatedStandardsSets]);
      setIsDeletingStandardsSet(false);
    }, standardsSetId);
  };

  const openConfirmModal = (mode: IStandardsSetMode) => {
    setStandardsSetMode(mode);
    setIsConfirmActionModalOpen(true);
  };

  const closeConfirmModal = (mode: IStandardsSetMode) => {
    setStandardsSetMode(
      mode === STANDARDS_SET_MODE.DELETE && isEditingSet
        ? "edit"
        : STANDARDS_SET_MODE.VIEW,
    );
    setIsConfirmActionModalOpen(false);
  };

  useEffect(() => {
    setNewStandardsSet({
      id: draftStandardsSetId || "",
      title: "",
      children: [],
      viewmodel_type: "list_type",
    });
  }, [createdStandardsSets.length, draftStandardsSetId]);

  useEffect(() => {
    getCreatedStandardsSets();
  }, [standardsSets]);

  return {
    newStandardsSet,
    setNewStandardsSet,
    createdStandardsSets,
    setCreatedStandardsSets,
    getCreatedStandardsSets,
    addNewStandardsSet,
    editStandardsSet,
    deleteStandardsSet,
    standardsSetTitleError,
    setStandardsSetTitleError,
    isEditingSet,
    setIsEditingSet,
    standardsSetMode,
    setStandardsSetMode,
    confirmModalData,
    setConfirmModalData,
    isConfirmActionModalOpen,
    setIsConfirmActionModalOpen,
    openConfirmModal,
    closeConfirmModal,
    draftStandardsSetId,
    setDraftStandardsSetId,
    handleCreateDraftStandardsSet,
    operationErrorMessage: operationErrorMessage.current,
    resetOperationErrorMessage,
    isPublishingStandardsSet,
    setIsPublishingStandardsSet,
    isUpdatingStandardsSet,
    setIsUpdatingStandardsSet,
    isDeletingStandardsSet,
    setIsDeletingStandardsSet,
    moveToAllTabBeforeDelete,
  };
};
